"use client";
import { useState } from "react";
import { IoClose } from "react-icons/io5";
import { MdSupportAgent } from "react-icons/md";
import { toast, Toaster } from "react-hot-toast";
import API_URLS from "./apiUrls";

export default function ContactForm() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    contactNumber: "",
    category: "",
    query: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [
    { value: "marketing", label: "Marketing / Sales" },
    { value: "technical", label: "Technical Support" },
    { value: "account", label: "Account Related" },
    { value: "other", label: "Other" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const requestBody = {
        mobileNumber: formData.contactNumber,
        query: formData.category,
        message: formData.query,
      };
      const response = await fetch(API_URLS.contact, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) throw new Error(`Server responded with status ${response.status}`);
      await response.json();
      setFormData({ contactNumber: "", category: "", query: "" });
      toast.success(
        <div>
          <div className="font-bold mb-1">Message sent successfully</div>
          <div className="text-sm opacity-90">We will get back soon</div>
        </div>,
        { position: "top-center", duration: 4000 }
      );
      setIsOpen(false);
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Something went wrong. Please try again later.", {
        position: "top-center",
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: { background: "#1e3a5f", color: "#f8fafc", borderRadius: "12px", padding: "12px 16px" },
          success: { iconTheme: { primary: "#4ade80", secondary: "#1e3a5f" } },
          error: { iconTheme: { primary: "#f87171", secondary: "#1e3a5f" } },
        }}
      />

      {/* ===== Floating Button ===== */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {/* Pulse ring */}
        <span className="absolute bottom-0 right-0 w-14 h-14 rounded-full bg-green-500 opacity-30 animate-ping pointer-events-none" />
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-14 h-14 bg-gradient-to-br from-green-500 to-green-700 text-white rounded-full shadow-2xl shadow-green-600/40 hover:scale-110 hover:shadow-green-600/60 transition-all duration-300 cursor-pointer flex items-center justify-center"
          aria-label="Open contact form"
        >
          {isOpen ? (
            <IoClose className="w-6 h-6 transition-transform duration-200 rotate-0" />
          ) : (
            <MdSupportAgent className="w-7 h-7" />
          )}
        </button>
      </div>

      {/* ===== Popup Card ===== */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-80 sm:w-96 z-50 animate-slideUp">
          {/* Glassmorphism card */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl shadow-slate-900/20 border border-white/60 overflow-hidden">

            {/* Header with gradient */}
            <div className="relative bg-gradient-to-r from-blue-600 to-blue-800 px-5 py-4 overflow-hidden">
              {/* Decorative circles */}
              <span className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-blue-400/20 blur-xl" />
              <span className="absolute bottom-0 left-8 w-12 h-12 rounded-full bg-white/10 blur-lg" />

              <div className="relative flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center border border-white/30">
                    <MdSupportAgent className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm tracking-wide">Instant Call Back</h3>
                    <p className="text-slate-400 text-xs mt-0.5">We'll reach you within minutes</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 cursor-pointer"
                >
                  <IoClose className="w-4 h-4 text-slate-300" />
                </button>
              </div>

              {/* Online indicator */}
              <div className="relative flex items-center gap-1.5 mt-3">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-green-300 text-xs font-medium">Agents online now</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">

              {/* Mobile Number */}
              <div className="group">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                  Mobile Number
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium select-none">+91</span>
                  <div className="absolute left-[52px] top-1/2 -translate-y-1/2 w-px h-4 bg-slate-200" />
                  <input
                    type="tel"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    required
                    placeholder="98765 43210"
                    className="w-full pl-16 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-500 focus:bg-white transition-all duration-200"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                  Query For
                </label>
                <div className="relative">
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-500 focus:bg-white transition-all duration-200 cursor-pointer"
                  >
                    <option value="" disabled>Select a category</option>
                    {categories.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  {/* Custom arrow */}
                  <div className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2">
                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">
                  Message
                </label>
                <textarea
                  name="query"
                  rows={3}
                  value={formData.query}
                  onChange={handleChange}
                  required
                  placeholder="Describe your query briefly..."
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-500 focus:bg-white transition-all duration-200"
                />
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full relative overflow-hidden bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 disabled:from-slate-300 disabled:to-slate-300 text-white font-semibold text-sm py-3 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 disabled:shadow-none transition-all duration-300 cursor-pointer disabled:cursor-not-allowed group"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      Request Call Back
                      <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </>
                  )}
                </span>
              </button>

              <p className="text-center text-xs text-slate-400">
                🔒 Your information is safe & confidential
              </p>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes slideUp {
          from { transform: translateY(24px) scale(0.97); opacity: 0; }
          to   { transform: translateY(0)    scale(1);    opacity: 1; }
        }
      `}</style>
    </>
  );
}